import { type ToolCallMessagePartComponent, type ToolCallMessagePartProps, useAuiState } from '@assistant-ui/react';
import { Button, Group, Pagination } from '@mantine/core';
import { IconArrowDown } from '@tabler/icons-react';
import { useState } from 'react';

import { type RevisionScanMessage, revisionView, type ToolCallRevision } from './revisions.ts';

/**
 * Revision-aware treatment around a registered tool render (see
 * `lib/tool/revisions.ts`). Treatment is derived from the thread at
 * render time — nothing is stored beyond the `replaces` edge on results:
 *
 * - superseded + adjacent (its replacement renders right below): nothing;
 * - superseded + distant: a slim jump-to-latest link, so the original
 *   spot is never a confusing hole;
 * - current revision of a multi-revision group: a stepper over the
 *   group's history, pinned to the latest revision;
 * - everything else: the tool's own render, untouched.
 */
export function RevisionBoundary<ArgsType extends Record<string, unknown>, ResultType>({
  Render,
  partProps,
}: {
  Render: ToolCallMessagePartComponent<ArgsType, ResultType>;
  partProps: ToolCallMessagePartProps<ArgsType, ResultType>;
}) {
  const toolCallId = useAuiState((state) => (state.part.type === 'tool-call' ? state.part.toolCallId : undefined));
  const messages = useAuiState((state) => state.thread.messages);
  const view = revisionView(messages as readonly RevisionScanMessage[], toolCallId);
  if (view.kind === 'superseded') {
    return view.adjacent ? null : <SupersededLink current={view.current} />;
  }
  if (view.kind === 'current') {
    return (
      <div data-tool-call-id={toolCallId}>
        <RevisionStepper members={view.members} partProps={partProps} Render={Render} />
      </div>
    );
  }
  return <Render {...partProps} />;
}

/**
 * Stepper over a revision group's history, rendered on the group's current
 * revision. Selection counts backwards from the latest revision (0 =
 * latest), so a newly streamed-in revision keeps the view pinned to the
 * newest one.
 */
function RevisionStepper<ArgsType extends Record<string, unknown>, ResultType>({
  members,
  partProps,
  Render,
}: {
  members: readonly ToolCallRevision[];
  partProps: ToolCallMessagePartProps<ArgsType, ResultType>;
  Render: ToolCallMessagePartComponent<ArgsType, ResultType>;
}) {
  const [stepsBack, setStepsBack] = useState(0);
  const back = Math.min(stepsBack, members.length - 1);
  const page = members.length - back;
  const selected = members.at(page - 1) as ToolCallRevision;
  return (
    <>
      <Group justify="flex-end">
        <Pagination
          onChange={(next) => setStepsBack(members.length - next)}
          size="xs"
          total={members.length}
          value={page}
        />
      </Group>
      {back === 0 ? (
        <Render {...partProps} />
      ) : (
        // Older revisions render through the same tool component with that
        // revision's own args/result swapped in at the props level. Part-scope
        // hooks inside the component still see the live (current) part — for
        // the args-driven tool renders this wraps, that is the intended view:
        // the old spec re-resolved against the current session.
        <Render
          {...({
            ...partProps,
            args: selected.args,
            result: selected.output,
            toolCallId: selected.toolCallId,
          } as typeof partProps)}
        />
      )}
    </>
  );
}

/**
 * Placeholder at a superseded call's original position when its
 * replacement is *not* directly adjacent. Scrolls to the group's current
 * revision; the old output itself stays reachable through the stepper on
 * that current revision.
 */
function SupersededLink({ current }: { current: ToolCallRevision }) {
  return (
    <Button
      color="dark.7"
      leftSection={<IconArrowDown size={14} />}
      onClick={() =>
        document
          .querySelector(`[data-tool-call-id="${CSS.escape(current.toolCallId)}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      size="compact-sm"
    >
      Revised — jump to the latest version
    </Button>
  );
}
